import { z } from 'zod';
import {
  LINE_ITEM_KIND, CUSTOMER_TYPE,
} from '../enums.js';

/**
 * z.enum helper that accepts our `as const` readonly enum arrays while preserving the literal
 * union type for inference. Avoids casting at every call site.
 */
export const zEnum = <const T extends readonly string[]>(vals: T) =>
  z.enum(vals as unknown as [T[number], ...T[number][]]);

/** Integer cents. Money is never a float. */
export const zCents = z.number().int('Amount must be whole cents');
export const zNonNegCents = zCents.min(0, 'Amount cannot be negative');

/** A percentage like tax (8.25) or discount (10). */
export const zPercent = z.number().min(0).max(100);

export const zEmail = z.string().trim().toLowerCase().email('Enter a valid email');
export const zPhone = z
  .string()
  .trim()
  .min(7, 'Enter a valid phone number')
  .max(25)
  .regex(/^[+()\-.\s\d]+$/, 'Enter a valid phone number');

export const zNonEmpty = z.string().trim().min(1, 'Required');
export const zTag = z.string().trim().min(1).max(40);

export const AddressSchema = z.object({
  line1: zNonEmpty.max(200),
  line2: z.string().trim().max(200).optional(),
  city: zNonEmpty.max(120),
  state: zNonEmpty.max(60),
  postalCode: zNonEmpty.max(20),
  country: z.string().trim().max(60).default('US'),
  // Optional geocode; MapProvider fills these later. Never required.
  lat: z.number().min(-90).max(90).optional(),
  lng: z.number().min(-180).max(180).optional(),
});
export type Address = z.infer<typeof AddressSchema>;

export const ContactSchema = z.object({
  name: zNonEmpty.max(120),
  email: zEmail.optional(),
  phone: zPhone.optional(),
  role: z.string().trim().max(60).optional(), // e.g. "Property Manager"
  isPrimary: z.boolean().default(false),
});
export type Contact = z.infer<typeof ContactSchema>;

/**
 * A single line on an estimate or invoice. `unitPrice` and computed `total` are in cents.
 * `internalCost` is optional (margin tracking) and never exposed to customers.
 */
export const LineItemSchema = z.object({
  kind: zEnum(LINE_ITEM_KIND),
  pricebookItemId: z.string().optional(),
  name: zNonEmpty.max(200),
  description: z.string().trim().max(2000).optional(),
  quantity: z.number().positive('Quantity must be greater than zero').default(1),
  unitPrice: zCents, // may be negative for discount kind
  taxable: z.boolean().default(true),
  internalCost: zNonNegCents.optional(),
});
export type LineItem = z.infer<typeof LineItemSchema>;

export const CustomerTypeSchema = zEnum(CUSTOMER_TYPE);

/** Base fields present on every persisted entity (server-owned; never accepted from clients). */
export const EntityMetaSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdByUserId: z.string().optional(),
  schemaVersion: z.number().int().default(1),
});
export type EntityMeta = z.infer<typeof EntityMetaSchema>;

/** Communication preferences on a customer. */
export const CommsPrefsSchema = z.object({
  email: z.boolean().default(true),
  sms: z.boolean().default(false), // requires explicit consent capture
  smsConsentAt: z.string().optional(),
  marketing: z.boolean().default(false),
});
export type CommsPrefs = z.infer<typeof CommsPrefsSchema>;
